import { ActivityActionType, ActivityLogItem } from 'ActivityLogModule/types/activityLog';

/**
 * Helpers for deriving display fields from the raw request snapshot that the
 * galaxy-server ActivityLogInterceptor stores in `details`
 * ({ method, path, params?, query?, body? }).
 *
 * GLX-1789: impersonation rows created before GLX-1255 carry no top-level
 * teamId, and the impersonation target has always lived only inside
 * `details.body.account_id`. These helpers surface both without requiring a
 * backfill.
 *
 * The interceptor logs BEFORE the request is handled, so a row exists even
 * when the request was rejected — values extracted here describe what the
 * request ASKED FOR, not what necessarily happened.
 */

/** Key aliases the galaxy-server interceptor accepts for a team identifier. */
const TEAM_ID_KEYS = ['teamId', 'team_id', 'mosaicTeamId'] as const;

/**
 * Canonical positive-integer id: 1-15 digits, no leading zero. Applied to the
 * string form of numbers too, so floats, negatives, NaN/Infinity, and >15-digit
 * values all fail the same gate regardless of input type.
 */
const CANONICAL_ID_PATTERN = /^[1-9]\d{0,14}$/;

/**
 * Canonical positive-integer id (number or digit string), else undefined.
 * Deliberately stricter than the interceptor's truthiness checks: request
 * bodies are caller-controlled, so garbage from rejected requests (0,
 * negative, non-numeric, leading-zero, or absurdly long values) must not
 * render as an id.
 */
const asIdString = (value: unknown): string | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  const candidate = typeof value === 'number' ? String(value) : value.trim();
  return CANONICAL_ID_PATTERN.test(candidate) ? candidate : undefined;
};

const findTeamIdIn = (source: unknown): string | undefined => {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of TEAM_ID_KEYS) {
    const id = asIdString((source as Record<string, unknown>)[key]);
    if (id) return id;
  }
  return undefined;
};

/**
 * Derive a team ID from the captured request details, mirroring the
 * galaxy-server interceptor's extraction order (params → query → body).
 * Used as a fallback for rows whose stored teamId is blank.
 */
export const getTeamIdFromDetails = (log: ActivityLogItem): string | undefined => {
  const details = log.details;
  if (!details) return undefined;
  return findTeamIdIn(details.params) ?? findTeamIdIn(details.query) ?? findTeamIdIn(details.body);
};

/** Team ID to display for a row: the stored column value, else the request snapshot. */
export const getDisplayTeamId = (log: ActivityLogItem): string | undefined =>
  asIdString(log.teamId) ?? getTeamIdFromDetails(log);

/**
 * For auth.impersonate rows, the account the request targeted
 * (`body.account_id` of the captured POST /rails_api/impersonate request —
 * the only key the endpoint accepts). The row may be a rejected attempt.
 */
export const getImpersonationTargetAccountId = (log: ActivityLogItem): string | undefined => {
  if (log.actionType !== ActivityActionType.authImpersonate) return undefined;
  const body = log.details?.body;
  if (!body || typeof body !== 'object') return undefined;
  return asIdString((body as Record<string, unknown>).account_id);
};
